import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../lib/utils";
import { BiologicalActivityDTO, ClientType, CodeType, CustomerCreationDTO, CustomerResponseDTO, CustomerUpdateDTO, EntryReason, FindAllCustomerByCodeResponseDTO, FindAllCustomersFilters, findAllCustomersResponseDTO } from "./dto";
import { NewCustomerService } from "./service";

interface CustomerState {
    selectedCustomerTab: number
    findAllCustomersStatus: PromiseStatuses
    findAllCustomersResponse?: findAllCustomersResponseDTO
    getCustomerByIdStatus: PromiseStatuses
    getCustomerByIdResponse?: CustomerResponseDTO
    findAllCustomersFilters: FindAllCustomersFilters
    customerCreateRequest: CustomerCreationDTO
    customerCreateStatus: PromiseStatuses
    customerUpdateStatus: PromiseStatuses
    customerUpdateRequest: CustomerUpdateDTO
    getCustomerByCodeStatus: PromiseStatuses
    getCustomerByCodeResponse?: FindAllCustomerByCodeResponseDTO
    customerCreateErrors: Partial<Record<keyof CustomerCreationDTO, string>>
}

const initialState: CustomerState = {
    selectedCustomerTab: 0,
    findAllCustomersStatus: "idle",
    findAllCustomersFilters: {
        itemsPerPage: 10,
        page: 1,
        order: false,
        active: null,
        norm: null,
        companyName: null,
        search: null,
        headquarterId: null,
        operatorsGroup: null
    },
    customerCreateRequest: {
        companyName: "",
        legalForm: "",
        cuaa: "",
        vat: "",
        address: "",
        countryRegion: "",
        province: "",
        city: "",
        zipCode: "",
        latitude: null,
        longitude: null,
        rpAddress: "",
        rpCountryRegion: "",
        rpCity: "",
        rpProvince: "",
        rpZipCode: "",
        addresseeCode: null,
        accountingCode: null,
        bankDetails: null,
        startingDate: null,
        entryReason: EntryReason.PRIMA_NOTIFICA,
        entryOdcId: null,
        takeoverDetails: null,
        endingDate: null,
        exitReason: null,
        exitOdcId: null,
        changeOperatorDetails: null,
        note: null,
        norm: "NORMA_BIO",
        active: null,
        clientCode: null,
        codeType: CodeType.NUOVO_CODICE,
        existingCode: null,
        clientType: ClientType.SINGOLO,
        operatorsGroup: null,
        leaderId: null,
        headQuarterIds: [],
        biologicalActivity: [],
        sqnpiSubjectType: null,
        sqnpiAdhesionType: null,
        sqProductionChain: null,
        zrCertificationType: null,
        zrTargetThreshold: null,
        zrAddingRequirements: null,
        zrType: null,
        ggGgn: null,
        ggStandard: null,
        ggEdition: null,
        ggApplicationField: null,
        ggCategory: null,
        ggIFACertificationOption: null,
        ggManipulation: null,
        ggParallelProperty: null,
        ggLabelling: null,
        ggLicenseGnnLabelLogo: null,
        ggPartialTransformation: null,
        ggCOCCertificationOption: null,
        ggAddOns: null
    },
    customerUpdateRequest: {
        companyName: "",
        legalForm: "",
        cuaa: "",
        vat: "",
        address: "",
        countryRegion: "",
        province: "",
        city: "",
        zipCode: "",
        latitude: null,
        longitude: null,
        rpAddress: "",
        rpCountryRegion: "",
        rpCity: "",
        rpProvince: "",
        rpZipCode: "",
        addresseeCode: null,
        accountingCode: null,
        bankDetails: null,
        startingDate: null,
        entryReason: EntryReason.PRIMA_NOTIFICA,
        entryOdcId: null,
        takeoverDetails: null,
        endingDate: null,
        exitReason: null,
        exitOdcId: null,
        changeOperatorDetails: null,
        note: null,
        norm: "NORMA_BIO",
        active: null,
        leaderId: null,
        headQuarterIds: [],
        biologicalActivity: [],
        sqnpiSubjectType: null,
        sqnpiAdhesionType: null,
        sqProductionChain: null,
        zrCertificationType: null,
        zrTargetThreshold: null,
        zrAddingRequirements: null,
        zrType: null,
        ggGgn: null,
        ggStandard: null,
        ggEdition: null,
        ggApplicationField: null,
        ggCategory: null,
        ggIFACertificationOption: null,
        ggManipulation: null,
        ggParallelProperty: null,
        ggLabelling: null,
        ggLicenseGnnLabelLogo: null,
        ggPartialTransformation: null,
        ggCOCCertificationOption: null,
        ggAddOns: null
    },
    getCustomerByIdStatus: "idle",
    customerCreateStatus: "idle",
    customerUpdateStatus: "idle",
    getCustomerByCodeStatus: "idle",
    customerCreateErrors: {}
}

export const validateCustomerCreateRequest = createAsyncThunk(
    'customer/validateCreateRequest',
    async (request: CustomerCreationDTO, thunkApi): Promise<void> => {
        const errors: Partial<Record<keyof CustomerCreationDTO, string>> = {}

        const requiredStrings: { field: keyof CustomerCreationDTO; label: string }[] = [
            { field: 'companyName', label: 'Ragione Sociale' },
            { field: 'legalForm', label: 'Forma Giuridica' },
            { field: 'cuaa', label: 'CUAA' },
            { field: 'vat', label: 'Partita IVA' },
            { field: 'address', label: 'Indirizzo Sede Legale' },
            { field: 'countryRegion', label: 'Regione' },
            { field: 'province', label: 'Provincia' },
            { field: 'city', label: 'Comune' },
            { field: 'zipCode', label: 'CAP' },
            { field: 'rpAddress', label: 'Indirizzo Sede Operativa' },
            { field: 'rpCountryRegion', label: 'Regione Sede Operativa' },
            { field: 'rpProvince', label: 'Provincia Sede Operativa' },
            { field: 'rpCity', label: 'Comune Sede Operativa' },
            { field: 'rpZipCode', label: 'CAP Sede Operativa' },
            { field: 'norm', label: 'Norma' },
        ]

        for (const { field, label } of requiredStrings) {
            const value = request[field]
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                errors[field] = `${label} è obbligatorio`
            }
        }

        if (!request.startingDate) errors['startingDate'] = 'Data Inizio è obbligatoria'
        if (!request.entryReason) errors['entryReason'] = 'Motivo di Ingresso è obbligatorio'
        if (!request.clientType) errors['clientType'] = 'Tipologia Cliente è obbligatoria'
        if (!request.headQuarterIds?.length) errors['headQuarterIds'] = 'Sede di Competenza è obbligatoria'

        if (Object.keys(errors).length > 0) {
            return thunkApi.rejectWithValue(errors) as never
        }
    }
)

export const createCustomer = createAsyncThunk(
    'customer/createCustomer',
    async (request: CustomerCreationDTO, thunkApi): Promise<number> => {
        const customerService = NewCustomerService()

        return customerService.createCustomer(request).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })

    },
)

export const updateCustomer = createAsyncThunk(
    'customer/updateCustomer',
    async (request: { id: string, data: CustomerUpdateDTO }, thunkApi): Promise<void> => {
        const customerService = NewCustomerService()

        return customerService.updateCustomer(request.id, request.data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })

    },
)

export const getCustomers = createAsyncThunk(
    'customer/getCustomers',
    async (filters: FindAllCustomersFilters, thunkApi): Promise<findAllCustomersResponseDTO> => {
        const customerService = NewCustomerService()

        return customerService.getCustomers(filters)
    },
)

export const getCustomerById = createAsyncThunk(
    'customer/getCustomerById',
    async (id: string, thunkApi): Promise<CustomerResponseDTO> => {
        const customerService = NewCustomerService()

        return customerService.getCustomerById(id)
    },
)

export const getCustomerByCode = createAsyncThunk(
    'customer/getCustomerByCode',
    async (code: string, thunkApi): Promise<FindAllCustomerByCodeResponseDTO> => {
        const customerService = NewCustomerService()

        return customerService.getCustomerByCode(code)
    },
)


const customerSlice = createSlice({
    name: 'customer/slice',
    initialState,
    reducers: {
        setSelectedCustomerTab: (state, action) => {
            state.selectedCustomerTab = action.payload
        },
        setCustomerCreateRequestField: (state, action: { payload: { field: keyof CustomerCreationDTO, value: any } }) => {
            state.customerCreateRequest = {
                ...state.customerCreateRequest,
                [action.payload.field]: action.payload.value
            }
        },
        setCustomerUpdateRequest: (state, action) => {
            state.customerUpdateRequest = action.payload
        },
        setCustomerUpdateRequestField: (state, action: { payload: { field: keyof CustomerUpdateDTO, value: any } }) => {
            state.customerUpdateRequest = {
                ...state.customerUpdateRequest,
                [action.payload.field]: action.payload.value
            }
        },
        setFindAllCustomerFilterField: (state, action: { payload: { field: keyof FindAllCustomersFilters, value: any } }) => {
            state.findAllCustomersFilters = {
                ...state.findAllCustomersFilters,
                [action.payload.field]: action.payload.value
            }
        },
        resetCustomersList: (state) => {
            state.findAllCustomersResponse = undefined
            state.findAllCustomersStatus = 'idle'
        },
        setCustomerCreateStatus: (state, action) => {
            state.customerCreateStatus = action.payload
        },
        setCustomerUpdateStatus: (state, action) => {
            state.customerUpdateStatus = action.payload
        },
        resetFindAllCustomersFilters: (state) => {
            state.findAllCustomersFilters = {
                itemsPerPage: 10,
                page: 1,
                order: false,
                active: null,
                norm: null,
                companyName: null,
                search: null,
                headquarterId: null,
                operatorsGroup: null
            }
        },
        resetCustomerCreateRequest: (state) => {
            state.customerCreateRequest = {
                companyName: "",
                legalForm: "",
                cuaa: "",
                vat: "",
                address: "",
                countryRegion: "",
                province: "",
                city: "",
                zipCode: "",
                latitude: null,
                longitude: null,
                rpAddress: "",
                rpCountryRegion: "",
                rpCity: "",
                rpProvince: "",
                rpZipCode: "",
                addresseeCode: null,
                accountingCode: null,
                bankDetails: null,
                startingDate: new Date(),
                entryReason: EntryReason.PRIMA_NOTIFICA,
                entryOdcId: null,
                takeoverDetails: null,
                endingDate: null,
                exitReason: null,
                exitOdcId: null,
                changeOperatorDetails: null,
                note: null,
                norm: "NORMA_BIO",
                active: null,
                clientCode: null,
                codeType: CodeType.NUOVO_CODICE,
                existingCode: null,
                clientType: ClientType.SINGOLO,
                operatorsGroup: null,
                leaderId: null,
                headQuarterIds: [],
                biologicalActivity: [],
                sqnpiSubjectType: null,
                sqnpiAdhesionType: null,
                sqProductionChain: null,
                zrCertificationType: null,
                zrTargetThreshold: null,
                zrAddingRequirements: null,
                zrType: null,
                ggGgn: null,
                ggStandard: null,
                ggEdition: null,
                ggApplicationField: null,
                ggCategory: null,
                ggIFACertificationOption: null,
                ggManipulation: null,
                ggParallelProperty: null,
                ggLabelling: null,
                ggLicenseGnnLabelLogo: null,
                ggPartialTransformation: null,
                ggCOCCertificationOption: null,
                ggAddOns: null
            }
        },
        resetCustomerUpdateRequest: (state) => {
            state.customerUpdateRequest = {
                companyName: "",
                legalForm: "",
                cuaa: "",
                vat: "",
                address: "",
                countryRegion: "",
                province: "",
                city: "",
                zipCode: "",
                latitude: null,
                longitude: null,
                rpAddress: "",
                rpCountryRegion: "",
                rpCity: "",
                rpProvince: "",
                rpZipCode: "",
                addresseeCode: null,
                accountingCode: null,
                bankDetails: null,
                startingDate: new Date(),
                entryReason: EntryReason.PRIMA_NOTIFICA,
                entryOdcId: null,
                takeoverDetails: null,
                endingDate: null,
                exitReason: null,
                exitOdcId: null,
                changeOperatorDetails: null,
                note: null,
                norm: "NORMA_BIO",
                active: null,
                leaderId: null,
                headQuarterIds: [],
                biologicalActivity: [],
                sqnpiSubjectType: null,
                sqnpiAdhesionType: null,
                sqProductionChain: null,
                zrCertificationType: null,
                zrTargetThreshold: null,
                zrAddingRequirements: null,
                zrType: null,
                ggGgn: null,
                ggStandard: null,
                ggEdition: null,
                ggApplicationField: null,
                ggCategory: null,
                ggIFACertificationOption: null,
                ggManipulation: null,
                ggParallelProperty: null,
                ggLabelling: null,
                ggLicenseGnnLabelLogo: null,
                ggPartialTransformation: null,
                ggCOCCertificationOption: null,
                ggAddOns: null
            }
        },
        setCustomerCreateErrors: (state, action: { payload: Partial<Record<keyof CustomerCreationDTO, string>> }) => {
            state.customerCreateErrors = action.payload
        },
        resetCustomerCreateErrors: (state) => {
            state.customerCreateErrors = {}
        },
        resetCustomerCreateError: (state, action: { payload: keyof CustomerCreationDTO }) => {
            delete state.customerCreateErrors[action.payload]
        },
        setCreateBiologicalActivity: (state, action) => {
            state.customerCreateRequest.biologicalActivity = action.payload
        },
        setCreateBiologicalActivityActivityByIndex: (state, action) => {
            state.customerCreateRequest.biologicalActivity[action.payload.index].activity = action.payload.data
        },
        setCreateBiologicalActivityCategoryByIndex: (state, action) => {
            state.customerCreateRequest.biologicalActivity[action.payload.index].category = action.payload.data
        },
        setCreateBiologicalActivityProductsByIndex: (state, action) => {
            state.customerCreateRequest.biologicalActivity[action.payload.index].products = action.payload.data
        },
        addDefaultBiologicalActivity: (state) => {
            state.customerCreateRequest.biologicalActivity.push({
                activity: '',
                category: [],
                products: []
            } as BiologicalActivityDTO)
        },
        setUpdateBiologicalActivity: (state, action) => {
            state.customerUpdateRequest.biologicalActivity = action.payload
        },
        setUpdateBiologicalActivityActivityByIndex: (state, action) => {
            state.customerUpdateRequest.biologicalActivity![action.payload.index].activity = action.payload.data
        },
        setUpdateBiologicalActivityCategoryByIndex: (state, action) => {
            state.customerUpdateRequest.biologicalActivity![action.payload.index].category = action.payload.data
        },
        setUpdateBiologicalActivityProductsByIndex: (state, action) => {
            state.customerUpdateRequest.biologicalActivity![action.payload.index].products = action.payload.data
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getCustomers.pending, (state) => {
                state.findAllCustomersStatus = 'loading'
            })
            .addCase(getCustomers.fulfilled, (state, action) => {
                state.findAllCustomersStatus = 'successfully'
                state.findAllCustomersResponse = action.payload
            })
            .addCase(getCustomers.rejected, (state) => {
                state.findAllCustomersStatus = 'failed'
            })
            .addCase(getCustomerById.pending, (state) => {
                state.getCustomerByIdStatus = 'loading'
            })
            .addCase(getCustomerById.fulfilled, (state, action) => {
                state.getCustomerByIdStatus = 'successfully'
                state.getCustomerByIdResponse = action.payload
            })
            .addCase(getCustomerById.rejected, (state) => {
                state.getCustomerByIdStatus = 'failed'
            })
            .addCase(createCustomer.pending, (state) => {
                state.customerCreateStatus = 'loading'
            })
            .addCase(createCustomer.fulfilled, (state, action) => {
                state.customerCreateStatus = 'successfully'
            })
            .addCase(createCustomer.rejected, (state) => {
                state.customerCreateStatus = 'failed'
            })
            .addCase(updateCustomer.pending, (state) => {
                state.customerUpdateStatus = 'loading'
            })
            .addCase(updateCustomer.fulfilled, (state, action) => {
                state.customerUpdateStatus = 'successfully'
            })
            .addCase(updateCustomer.rejected, (state) => {
                state.customerUpdateStatus = 'failed'
            })
            .addCase(getCustomerByCode.pending, (state) => {
                state.getCustomerByCodeStatus = 'loading'
            })
            .addCase(getCustomerByCode.fulfilled, (state, action) => {
                state.getCustomerByCodeStatus = 'successfully'
                state.getCustomerByCodeResponse = action.payload
            })
            .addCase(getCustomerByCode.rejected, (state) => {
                state.getCustomerByCodeStatus = 'failed'
            })
            .addCase(validateCustomerCreateRequest.fulfilled, (state) => {
                state.customerCreateErrors = {}
            })
            .addCase(validateCustomerCreateRequest.rejected, (state, action) => {
                state.customerCreateErrors = action.payload ?? {}
            })
    },
})

export const {
    setSelectedCustomerTab,
    setCustomerCreateRequestField,
    setCustomerUpdateRequestField,
    setFindAllCustomerFilterField,
    resetCustomersList,
    resetFindAllCustomersFilters,
    resetCustomerCreateRequest,
    resetCustomerUpdateRequest,
    setCustomerCreateErrors,
    resetCustomerCreateErrors,
    resetCustomerCreateError,
    setCreateBiologicalActivity,
    setCreateBiologicalActivityActivityByIndex,
    setCreateBiologicalActivityCategoryByIndex,
    setCreateBiologicalActivityProductsByIndex,
    addDefaultBiologicalActivity,
    setUpdateBiologicalActivity,
    setUpdateBiologicalActivityActivityByIndex,
    setUpdateBiologicalActivityCategoryByIndex,
    setUpdateBiologicalActivityProductsByIndex,
    setCustomerUpdateRequest,
    setCustomerCreateStatus,
    setCustomerUpdateStatus
} = customerSlice.actions

export default customerSlice.reducer